import { useState } from 'react'
import { Project } from '../data/projectsData'
import './ProjectsSection.css'

interface ProjectsSectionProps {
  projects: Project[]
}

export default function ProjectsSection({ projects }: ProjectsSectionProps) {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [currentScreenshotIndex, setCurrentScreenshotIndex] = useState(0)
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null)

  const openProject = (project: Project) => {
    setSelectedProject(project)
    setCurrentScreenshotIndex(0)
  }

  const closeProject = () => {
    setSelectedProject(null)
  }

  const nextScreenshot = () => {
    if (selectedProject) {
      setCurrentScreenshotIndex((prev) =>
        (prev + 1) % selectedProject.screenshots.length
      )
    }
  }

  const prevScreenshot = () => {
    if (selectedProject) {
      setCurrentScreenshotIndex((prev) =>
        prev === 0 ? selectedProject.screenshots.length - 1 : prev - 1
      )
    }
  }

  return (
    <>
      <section className="projects-section">
        <div className="projects-header">
          <h2 className="projects-title">Projects</h2>
          <p className="projects-subtitle">
            A selection of projects I've worked on, showcasing various technologies and solutions
          </p>
        </div>

        <div className="projects-grid">
          {projects.map((project) => (
            <div
              key={project.id}
              className="project-card"
              onClick={() => openProject(project)}
            >
              <div className="project-banner">
                <img src={project.bannerImage} alt={`${project.title} banner`} />
              </div>
              <div className="project-card-header">
                <h3 className="project-title">{project.title}</h3>
                <span className="project-company">{project.company}</span>
              </div>
              <p className="project-period">{project.period}</p>
              <p className="project-description">{project.description}</p>
              <div className="project-highlights">
                {project.results.slice(0, 2).map((result, index) => (
                  <div key={index} className="highlight-item">
                    <span className="highlight-icon">• </span>
                    <span>{result}</span>
                  </div>
                ))}
              </div>
              <div className="project-card-footer">
                <span className="view-project">View Project •</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {selectedProject && (
        <div className="project-modal" onClick={closeProject}>
          <div className="project-window" onClick={(e) => e.stopPropagation()}>
            <div className="window-header">
              <div className="window-controls">
                <span className="control close" onClick={closeProject}></span>
                <span className="control minimize"></span>
                <span className="control maximize"></span>
              </div>
              <span className="window-title">{selectedProject.title}</span>
            </div>

            <div className="window-content">
              <div className="project-details">
                <div className="project-info">
                  <h2>{selectedProject.title}</h2>
                  <h3>{selectedProject.company}</h3>
                  <p className="period">{selectedProject.period}</p>
                  <p className="description">{selectedProject.description}</p>

                  <div className="key-features">
                    <h4>Key Features</h4>
                    <ul>
                      {selectedProject.keyFeatures.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="results">
                    <h4>Results</h4>
                    <ul>
                      {selectedProject.results.map((result, index) => (
                        <li key={index}>{result}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="process">
                    <h4>How It Works</h4>
                    <div className="process-steps">
                      {selectedProject.process.map((step, index) => (
                        <div key={index} className="step-item">
                          <span className="step-number">{index + 1}</span>
                          <p>{step}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="project-visuals">
                  <div className="project-screenshots">
                    <h4>Screenshots</h4>
                    <div className="screenshot-container">
                      <div className="screenshot-nav">
                        <button
                          className="nav-btn prev"
                          onClick={prevScreenshot}
                          disabled={selectedProject.screenshots.length <= 1}
                        >
                          &lt;
                        </button>
                        <span className="screenshot-counter">
                          {currentScreenshotIndex + 1} / {selectedProject.screenshots.length}
                        </span>
                        <button
                          className="nav-btn next"
                          onClick={nextScreenshot}
                          disabled={selectedProject.screenshots.length <= 1}
                        >
                          &gt;
                        </button>
                      </div>
                      <img
                        src={selectedProject.screenshots[currentScreenshotIndex]}
                        alt={`Screenshot ${currentScreenshotIndex + 1}`}
                        className="screenshot"
                        onClick={() => setEnlargedImage(selectedProject.screenshots[currentScreenshotIndex])}
                      />
                    </div>
                  </div>

                  {selectedProject.demoVideo && (
                    <div className="demo-video">
                      <h4>Demo Video</h4>
                      <a
                        href={selectedProject.demoVideo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="demo-video-link"
                      >
                        <span className="video-icon">▶</span>
                        Watch Demo on X
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {enlargedImage && (
        <div className="enlarged-image-modal" onClick={() => setEnlargedImage(null)}>
          <img
            src={enlargedImage}
            alt="Enlarged screenshot"
            className="enlarged-image"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="close-enlarged"
            onClick={() => setEnlargedImage(null)}
          >
            ×
          </button>
        </div>
      )}
    </>
  )
}